import type { SummaryEntry } from '@/lib/summaryParser';

interface BreadcrumbsProps {
  repoFullName: string;
  currentPath: string;
  summaryEntries?: SummaryEntry[] | null;
  onNavigate: (path: string) => void;
}

interface Crumb {
  label: string;
  path?: string;
}

function findBreadcrumbTrail(entries: SummaryEntry[], targetPath: string): Crumb[] | null {
  for (const entry of entries) {
    if (entry.path === targetPath) {
      return [{ label: entry.title, path: entry.path }];
    }
    if (entry.children.length > 0) {
      const child = findBreadcrumbTrail(entry.children, targetPath);
      if (child) {
        return [{ label: entry.title, path: entry.path }, ...child];
      }
    }
  }
  return null;
}

export function Breadcrumbs({ repoFullName, currentPath, summaryEntries, onNavigate }: BreadcrumbsProps) {
  let crumbs: Crumb[];

  if (summaryEntries && summaryEntries.length > 0) {
    const trail = findBreadcrumbTrail(summaryEntries, currentPath);
    crumbs = trail
      ? [{ label: repoFullName.split('/')[1] }, ...trail]
      : buildPathCrumbs(repoFullName, currentPath);
  } else {
    crumbs = buildPathCrumbs(repoFullName, currentPath);
  }

  return (
    <nav className="breadcrumbs">
      {crumbs.map((crumb, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <span className="separator">/</span>}
          {crumb.path && i < crumbs.length - 1 ? (
            <button onClick={() => onNavigate(crumb.path!)}>
              {crumb.label}
            </button>
          ) : (
            <span>{crumb.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

function buildPathCrumbs(repoFullName: string, path: string): Crumb[] {
  const repoName = repoFullName.split('/')[1];
  const parts = path.split('/');
  const crumbs: Crumb[] = [{ label: repoName }];

  for (let i = 0; i < parts.length; i++) {
    const label = i === parts.length - 1
      ? parts[i].replace('.md', '')
      : parts[i];
    crumbs.push({ label });
  }

  return crumbs;
}
